import React, { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import * as Sentry from "@sentry/react-native";

import UserNotificationWinkItem from "./Components/UserNotificationWinkItem";
import UserNotificationImageLikeItem from "./Components/UserNotificationImageLikeItem";
import UserNotificationFollowingImageUploadItem from "./Components/UserNotificationFollowingImageUploadItem";
import UserNotificationNewFollowerItem from "./Components/UserNotificationNewFollowerItem";

import {
  type UserNotification,
  UserNotificationType,
} from "@/schemas/user/notification";
import type { AppUserSession } from "@/schemas/auth/session";
import ScreenLoadingIcon from "@/Components/Icons/Loading/ScreenLoadingIcon";
import EmptyContentView from "@/Components/EmptyState/EmptyContentView";
import UserNotificationService from "@/services/user/notification";
import { useSession } from "@/context/auth/useSession";

type Props = {
  session: AppUserSession;
};

const PAGINATION_LIMIT = 15;

function UserNotificationsScreen({ session }: Props): React.JSX.Element {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMarkingAllNotificationsAsRead, setIsMarkingAllNotificationsAsRead] =
    useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { setIdentity, refreshUserIdentity } = useSession();

  const fetchMoreNotifications = async (): Promise<void> => {
    if (!nextCursor) return;

    try {
      setIsLoadingMore(true);
      const userNotificationService = new UserNotificationService(session);
      const response = await userNotificationService.getNotifications({
        limit: PAGINATION_LIMIT,
        next: nextCursor,
      });
      setNotifications([...notifications, ...response.items]);
      setNextCursor(response.next);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const markNotificationAsRead = async (
    notificationId: string,
  ): Promise<void> => {
    // update unreadNotificationsCount in UserIdentity locally
    const updatedLocalIdentity = {
      ...session.identity,
      unreadNotificationsCount: Math.max(
        session.identity.unreadNotificationsCount - 1,
        0,
      ),
    };
    setIdentity(updatedLocalIdentity);

    // mark notification as read in the backend
    const userNotificationService = new UserNotificationService(session);
    try {
      await userNotificationService.markNotificationAsRead(notificationId);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);

      // Revert the local state change if the API call fails
      const updatedLocalIdentity = {
        ...session.identity,
        unreadNotificationsCount: Math.max(
          session.identity.unreadNotificationsCount + 1,
          0,
        ),
      };
      setIdentity(updatedLocalIdentity);
    }
  };

  const markAllNotificationsAsRead = async (): Promise<void> => {
    setIsMarkingAllNotificationsAsRead(true);

    const userNotificationService = new UserNotificationService(session);
    try {
      // Mark all notifications as read in the backend
      await userNotificationService.markAllNotificationsAsRead();

      // Update local state to mark all notifications as read
      setNotifications(
        notifications.map((notification) => ({
          ...notification,
          isRead: true,
        })),
      );

      // Update unreadNotificationsCount in UserIdentity locally
      const updatedLocalIdentity = {
        ...session.identity,
        unreadNotificationsCount: 0,
      };
      setIdentity(updatedLocalIdentity);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert(
        "Something went wrong while marking all notifications as read",
        "Please try again later.",
        [{ text: "OK" }],
      );
    } finally {
      setIsMarkingAllNotificationsAsRead(false);
    }
  };

  const fetchNotifications = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const userNotificationService = new UserNotificationService(session);
      const response = await userNotificationService.getNotifications({
        limit: PAGINATION_LIMIT,
        next: null,
      });
      setNotifications(response.items);
      setNextCursor(response.next);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []); // TODO: Add session to the dependency array would cause fetchNotifications to be called when a notification is marked as read

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const renderItem = ({
    item,
  }: {
    item: UserNotification;
  }): React.JSX.Element => {
    switch (item.type) {
      case UserNotificationType.IMAGE_LIKE:
        return (
          <UserNotificationImageLikeItem
            userNotification={item}
            handleMarkNotificationAsRead={() => markNotificationAsRead(item.id)}
          />
        );
      case UserNotificationType.WINK:
        return (
          <UserNotificationWinkItem
            session={session}
            userNotification={item}
            handleMarkNotificationAsRead={() => markNotificationAsRead(item.id)}
          />
        );
      case UserNotificationType.NEW_FOLLOWER:
        return (
          <UserNotificationNewFollowerItem
            session={session}
            userNotification={item}
            handleMarkNotificationAsRead={() => markNotificationAsRead(item.id)}
          />
        );
      case UserNotificationType.FOLLOWING_IMAGE_UPLOAD:
        return (
          <UserNotificationFollowingImageUploadItem
            userNotification={item}
            handleMarkNotificationAsRead={() => markNotificationAsRead(item.id)}
          />
        );
      default:
        return <></>;
    }
  };

  const handleRefresh = async (): Promise<void> => {
    setRefreshing(true);
    refreshUserIdentity();
    const userNotificationService = new UserNotificationService(session);
    const existingNotificationsMap = new Map(
      notifications.map((notification) => [notification.id, notification]),
    );

    try {
      // Todo; we need to create a new endpoint with a timestamp filter to fetch the latest notifications.
      // e.g., getLatestNotifications({beforeAt: X, limit: 10})
      const response = await userNotificationService.getNotifications({
        limit: 5,
        next: null,
      });
      const newNotifications = response.items.filter(
        (notification) => !existingNotificationsMap.has(notification.id),
      );
      if (newNotifications.length > 0) {
        setNotifications([...newNotifications, ...notifications]);
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <View style={styles.container}>
      {isLoading ? (
        <ScreenLoadingIcon />
      ) : notifications.length > 0 ? (
        <FlatList
          data={notifications}
          renderItem={(item) => renderItem(item)}
          keyExtractor={(item) => item.id}
          onEndReached={fetchMoreNotifications}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
          showsVerticalScrollIndicator={false}
          ListHeaderComponent={
            <View style={styles.notificationHeaderContainer}>
              <Pressable
                style={({ pressed }) => [
                  styles.markAllAsReadContainer,
                  { opacity: pressed ? 0.5 : 1 },
                ]}
                onPress={markAllNotificationsAsRead}
                disabled={
                  isMarkingAllNotificationsAsRead ||
                  session.identity.unreadNotificationsCount === 0
                }
              >
                {isMarkingAllNotificationsAsRead ? (
                  <ActivityIndicator size="small" />
                ) : (
                  <Ionicons
                    name={"checkmark-done"}
                    size={24}
                    color={
                      session.identity.unreadNotificationsCount === 0
                        ? "gray"
                        : "black"
                    }
                  />
                )}
                <Text
                  style={[
                    isMarkingAllNotificationsAsRead ||
                    session.identity.unreadNotificationsCount === 0
                      ? { color: "gray" }
                      : { color: "black" },
                  ]}
                >
                  {" "}
                  Read All
                </Text>
              </Pressable>
            </View>
          }
          ListFooterComponent={
            isLoadingMore ? (
              <ActivityIndicator
                size="small"
                style={styles.loadMoreActivityIndicator}
              />
            ) : null
          }
        />
      ) : (
        <EmptyContentView
          emptyTitle={"No notifications yet!"}
          primaryMaterialCommunityIconName={"bell"}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    flex: 1,
  },
  notificationHeaderContainer: {
    height: 24,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  markAllAsReadContainer: {
    marginLeft: "auto",
    flexDirection: "row",
    alignItems: "center",
  },
  loadMoreActivityIndicator: {
    marginVertical: 10,
  },
});

export default UserNotificationsScreen;
